import React, { useState } from 'react';
import { 
  Video, 
  Code, 
  Copy, 
  ExternalLink, 
  CheckCircle,
  Play,
  Settings,
  Palette,
  Monitor
} from 'lucide-react';
import { VideoRoom } from './video-sdk/VideoRoom';

export const VideoSDKDemo: React.FC = () => {
  const [activeDemo, setActiveDemo] = useState<'basic' | 'advanced' | 'custom'>('basic');
  const [copiedCode, setCopiedCode] = useState<string | null>(null);
  const [demoConfig, setDemoConfig] = useState({
    roomName: 'demo-room-' + Math.random().toString(36).substr(2, 9),
    participantName: 'Demo User',
    theme: 'auto' as const,
    layout: 'grid' as const,
    features: {
      audio: true,
      video: true,
      screenShare: true,
      chat: false,
      recording: false
    }
  });

  const copyToClipboard = async (code: string, id: string) => {
    try {
      await navigator.clipboard.writeText(code);
      setCopiedCode(id);
      setTimeout(() => setCopiedCode(null), 2000);
    } catch (err) {
      console.error('Failed to copy code:', err);
    }
  };

  const basicUsageCode = `import { VideoRoom } from '@customer-connects/video-sdk';

function MyApp() {
  return (
    <VideoRoom
      apiKey="your-api-key"
      roomName="my-room"
      participantName="John Doe"
    />
  );
}`;

  const advancedUsageCode = `import { VideoRoom } from '@customer-connects/video-sdk';

function MyApp() {
  return (
    <VideoRoom
      apiKey="your-api-key"
      roomName="my-room"
      participantName="John Doe"
      theme="dark"
      layout="focus"
      features={{
        audio: true,
        video: true,
        screenShare: true,
        chat: true,
        recording: false
      }}
      onJoinRoom={() => console.log('Joined room')}
      onLeaveRoom={() => console.log('Left room')}
      onParticipantJoin={(participant) => 
        console.log('Participant joined:', participant)
      }
      onError={(error) => console.error('Video error:', error)}
    />
  );
}`;

  const installationCode = `# Install the SDK
npm install @customer-connects/video-sdk

# Or with yarn
yarn add @customer-connects/video-sdk`;

  const customThemeCode = `import { VideoRoom } from '@customer-connects/video-sdk';

// Custom CSS variables for theming
const customTheme = {
  '--cc-primary-color': '#6366f1',
  '--cc-background-color': '#1f2937',
  '--cc-text-color': '#ffffff',
  '--cc-border-radius': '12px'
};

function MyApp() {
  return (
    <div style={customTheme}>
      <VideoRoom
        apiKey="your-api-key"
        roomName="themed-room"
        participantName="John Doe"
        className="custom-video-room"
      />
    </div>
  );
}`;

  const features = [
    {
      icon: <Video className="w-6 h-6" />,
      title: 'High-Quality Video',
      description: 'Crystal clear HD video with adaptive bitrate and automatic quality adjustment'
    },
    {
      icon: <Monitor className="w-6 h-6" />,
      title: 'Screen Sharing',
      description: 'Share your screen, specific applications, or browser tabs with one click'
    },
    {
      icon: <Settings className="w-6 h-6" />,
      title: 'Configurable Features',
      description: 'Enable/disable audio, video, chat, recording, and other features as needed'
    },
    {
      icon: <Palette className="w-6 h-6" />,
      title: 'Custom Theming',
      description: 'Fully customizable UI with dark/light themes and brand customization'
    }
  ];

  const handleConfigChange = (key: string, value: any) => {
    if (key.includes('.')) {
      const [parent, child] = key.split('.');
      setDemoConfig(prev => {
        const parentValue = prev[parent as keyof typeof prev];
        return {
          ...prev,
          [parent]: {
            ...(typeof parentValue === 'object' && parentValue !== null ? parentValue : {}),
            [child]: value
          }
        };
      });
    } else {
      setDemoConfig(prev => ({
        ...prev,
        [key]: value
      }));
    }
  };

  return (
    <div className="space-y-8">
      {/* Header */}
      <div className="text-center">
        <h1 className="text-4xl font-bold text-light-text-primary dark:text-dark-text-primary mb-4">
          Customer Connects Video SDK
        </h1>
        <p className="text-xl text-light-text-secondary dark:text-dark-text-secondary max-w-3xl mx-auto">
          A production-ready React video conferencing library. Drop it into any React app and get 
          enterprise-grade video calling in minutes.
        </p>
      </div>

      {/* Features Grid */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
        {features.map((feature, index) => (
          <div key={index} className="bg-light-bg-secondary dark:bg-dark-bg-secondary rounded-xl p-6 border border-light-border dark:border-dark-border">
            <div className="text-primary-500 mb-4">
              {feature.icon}
            </div>
            <h3 className="text-lg font-semibold text-light-text-primary dark:text-dark-text-primary mb-2">
              {feature.title}
            </h3>
            <p className="text-light-text-secondary dark:text-dark-text-secondary text-sm">
              {feature.description}
            </p>
          </div>
        ))}
      </div>

      {/* Demo Tabs */}
      <div className="bg-light-bg-secondary dark:bg-dark-bg-secondary rounded-xl border border-light-border dark:border-dark-border overflow-hidden">
        <div className="border-b border-light-border dark:border-dark-border">
          <div className="flex">
            {[
              { id: 'basic', label: 'Basic Usage', icon: Play },
              { id: 'advanced', label: 'Advanced Features', icon: Settings },
              { id: 'custom', label: 'Custom Theming', icon: Palette }
            ].map(({ id, label, icon: Icon }) => (
              <button
                key={id}
                onClick={() => setActiveDemo(id as any)}
                className={`flex items-center px-6 py-4 text-sm font-medium border-r border-light-border dark:border-dark-border ${
                  activeDemo === id
                    ? 'bg-primary-50 dark:bg-primary-900/20 text-primary-600 dark:text-primary-400 border-b-2 border-primary-500'
                    : 'text-light-text-secondary dark:text-dark-text-secondary hover:text-light-text-primary dark:hover:text-dark-text-primary'
                }`}
              >
                <Icon size={16} className="mr-2" />
                {label}
              </button>
            ))}
          </div>
        </div>

        <div className="p-6">
          {activeDemo === 'basic' && (
            <div className="space-y-6">
              <div>
                <h3 className="text-lg font-semibold text-light-text-primary dark:text-dark-text-primary mb-4">
                  Getting Started
                </h3>
                
                {/* Installation */}
                <div className="mb-6">
                  <h4 className="text-md font-medium text-light-text-primary dark:text-dark-text-primary mb-2">
                    1. Installation
                  </h4>
                  <div className="relative">
                    <pre className="bg-gray-900 text-gray-100 p-4 rounded-lg overflow-x-auto text-sm">
                      <code>{installationCode}</code>
                    </pre>
                    <button
                      onClick={() => copyToClipboard(installationCode, 'install')}
                      className="absolute top-2 right-2 p-2 bg-gray-800 text-gray-200 rounded hover:bg-gray-700"
                    >
                      {copiedCode === 'install' ? <CheckCircle size={16} /> : <Copy size={16} />}
                    </button>
                  </div>
                </div>

                {/* Basic Usage */}
                <div className="mb-6">
                  <h4 className="text-md font-medium text-light-text-primary dark:text-dark-text-primary mb-2">
                    2. Basic Usage
                  </h4>
                  <div className="relative">
                    <pre className="bg-gray-900 text-gray-100 p-4 rounded-lg overflow-x-auto text-sm">
                      <code>{basicUsageCode}</code>
                    </pre>
                    <button
                      onClick={() => copyToClipboard(basicUsageCode, 'basic')}
                      className="absolute top-2 right-2 p-2 bg-gray-800 text-gray-200 rounded hover:bg-gray-700"
                    >
                      {copiedCode === 'basic' ? <CheckCircle size={16} /> : <Copy size={16} />}
                    </button>
                  </div>
                </div>
              </div>
            </div>
          )}

          {activeDemo === 'advanced' && (
            <div className="space-y-6">
              <div>
                <h3 className="text-lg font-semibold text-light-text-primary dark:text-dark-text-primary mb-4">
                  Advanced Configuration
                </h3>
                
                <div className="relative">
                  <pre className="bg-gray-900 text-gray-100 p-4 rounded-lg overflow-x-auto text-sm">
                    <code>{advancedUsageCode}</code>
                  </pre>
                  <button
                    onClick={() => copyToClipboard(advancedUsageCode, 'advanced')}
                    className="absolute top-2 right-2 p-2 bg-gray-800 text-gray-200 rounded hover:bg-gray-700"
                  >
                    {copiedCode === 'advanced' ? <CheckCircle size={16} /> : <Copy size={16} />}
                  </button>
                </div>
              </div>
            </div>
          )}

          {activeDemo === 'custom' && (
            <div className="space-y-6">
              <div>
                <h3 className="text-lg font-semibold text-light-text-primary dark:text-dark-text-primary mb-4">
                  Custom Theming
                </h3>
                
                <div className="relative">
                  <pre className="bg-gray-900 text-gray-100 p-4 rounded-lg overflow-x-auto text-sm">
                    <code>{customThemeCode}</code>
                  </pre>
                  <button
                    onClick={() => copyToClipboard(customThemeCode, 'custom')}
                    className="absolute top-2 right-2 p-2 bg-gray-800 text-gray-200 rounded hover:bg-gray-700"
                  >
                    {copiedCode === 'custom' ? <CheckCircle size={16} /> : <Copy size={16} />}
                  </button>
                </div>
              </div>
            </div>
          )}
        </div>
      </div>

      {/* Interactive Demo */}
      <div className="bg-light-bg-secondary dark:bg-dark-bg-secondary rounded-xl border border-light-border dark:border-dark-border overflow-hidden">
        <div className="p-6 border-b border-light-border dark:border-dark-border">
          <h3 className="text-lg font-semibold text-light-text-primary dark:text-dark-text-primary mb-4">
            Interactive Demo
          </h3>
          
          {/* Demo Configuration */}
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4 mb-6">
            <div>
              <label className="block text-sm font-medium text-light-text-primary dark:text-dark-text-primary mb-2">
                Room Name
              </label>
              <input
                type="text"
                value={demoConfig.roomName}
                onChange={(e) => handleConfigChange('roomName', e.target.value)}
                className="w-full px-3 py-2 bg-light-bg-tertiary dark:bg-dark-bg-tertiary border border-light-border dark:border-dark-border rounded-lg text-light-text-primary dark:text-dark-text-primary text-sm"
              />
            </div>
            
            <div>
              <label className="block text-sm font-medium text-light-text-primary dark:text-dark-text-primary mb-2">
                Participant Name
              </label>
              <input
                type="text"
                value={demoConfig.participantName}
                onChange={(e) => handleConfigChange('participantName', e.target.value)}
                className="w-full px-3 py-2 bg-light-bg-tertiary dark:bg-dark-bg-tertiary border border-light-border dark:border-dark-border rounded-lg text-light-text-primary dark:text-dark-text-primary text-sm"
              />
            </div>
            
            <div>
              <label className="block text-sm font-medium text-light-text-primary dark:text-dark-text-primary mb-2">
                Theme
              </label>
              <select
                value={demoConfig.theme}
                onChange={(e) => handleConfigChange('theme', e.target.value)}
                className="w-full px-3 py-2 bg-light-bg-tertiary dark:bg-dark-bg-tertiary border border-light-border dark:border-dark-border rounded-lg text-light-text-primary dark:text-dark-text-primary text-sm"
              >
                <option value="auto">Auto</option>
                <option value="light">Light</option>
                <option value="dark">Dark</option>
              </select>
            </div>
            
            <div>
              <label className="block text-sm font-medium text-light-text-primary dark:text-dark-text-primary mb-2">
                Layout
              </label>
              <select
                value={demoConfig.layout}
                onChange={(e) => handleConfigChange('layout', e.target.value)}
                className="w-full px-3 py-2 bg-light-bg-tertiary dark:bg-dark-bg-tertiary border border-light-border dark:border-dark-border rounded-lg text-light-text-primary dark:text-dark-text-primary text-sm"
              >
                <option value="grid">Grid</option>
                <option value="focus">Focus</option>
                <option value="sidebar">Sidebar</option>
              </select>
            </div>
          </div>

          {/* Feature Toggles */}
          <div className="flex flex-wrap gap-4 mb-6">
            {Object.entries(demoConfig.features).map(([feature, enabled]) => (
              <label key={feature} className="flex items-center space-x-2">
                <input
                  type="checkbox"
                  checked={enabled}
                  onChange={(e) => handleConfigChange(`features.${feature}`, e.target.checked)}
                  className="rounded border-gray-300 text-primary-600 focus:ring-primary-500"
                />
                <span className="text-sm text-light-text-primary dark:text-dark-text-primary capitalize">
                  {feature.replace(/([A-Z])/g, ' $1').trim()}
                </span>
              </label>
            ))}
          </div>
        </div>

        {/* Demo Component */}
        <div className="p-6">
          <div className="h-96 bg-gray-100 dark:bg-gray-800 rounded-lg flex items-center justify-center">
            <div className="text-center">
              <Video className="w-16 h-16 text-gray-400 mx-auto mb-4" />
              <h4 className="text-lg font-medium text-light-text-primary dark:text-dark-text-primary mb-2">
                Interactive Demo Coming Soon
              </h4>
              <p className="text-light-text-secondary dark:text-dark-text-secondary mb-4">
                We're working on a live interactive demo. For now, use the code examples above to integrate the SDK.
              </p>
              <div className="flex justify-center space-x-4">
                <button className="px-4 py-2 bg-primary-500 text-white rounded-lg hover:bg-primary-600 transition-colors flex items-center">
                  <ExternalLink size={16} className="mr-2" />
                  View on GitHub
                </button>
                <button className="px-4 py-2 border border-light-border dark:border-dark-border text-light-text-primary dark:text-dark-text-primary rounded-lg hover:bg-light-bg-tertiary dark:hover:bg-dark-bg-tertiary transition-colors flex items-center">
                  <Code size={16} className="mr-2" />
                  Documentation
                </button>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* NPM Package Info */}
      <div className="bg-gradient-to-r from-blue-50 to-purple-50 dark:from-blue-900/20 dark:to-purple-900/20 rounded-xl p-6 border border-blue-200 dark:border-blue-800">
        <div className="flex items-start space-x-4">
          <div className="bg-blue-500 p-3 rounded-lg">
            <Code className="w-6 h-6 text-white" />
          </div>
          <div className="flex-1">
            <h3 className="text-lg font-semibold text-light-text-primary dark:text-dark-text-primary mb-2">
              Ready to Ship
            </h3>
            <p className="text-light-text-secondary dark:text-dark-text-secondary mb-4">
              The Customer Connects Video SDK is production-ready and will be available as an NPM package. 
              It's built on top of our robust Video API and Cloudflare RealtimeKit infrastructure.
            </p>
            <div className="flex flex-wrap gap-2">
              <span className="px-3 py-1 bg-green-100 dark:bg-green-900/20 text-green-700 dark:text-green-300 text-sm rounded-full">
                TypeScript Support
              </span>
              <span className="px-3 py-1 bg-blue-100 dark:bg-blue-900/20 text-blue-700 dark:text-blue-300 text-sm rounded-full">
                React 18 Compatible
              </span>
              <span className="px-3 py-1 bg-purple-100 dark:bg-purple-900/20 text-purple-700 dark:text-purple-300 text-sm rounded-full">
                Tree Shakeable
              </span>
              <span className="px-3 py-1 bg-orange-100 dark:bg-orange-900/20 text-orange-700 dark:text-orange-300 text-sm rounded-full">
                Zero Dependencies
              </span>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};